import React, { useState, useRef, useEffect } from "react";
import "./CountryDropdown.css";

const CountryDropdown = ({
    id,
    name,
    value,
    onChange,
    placeholder = "Select country",
    label,
    required = false,
    countries
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [hoveredIndex, setHoveredIndex] = useState(-1);
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);

    // Filter countries based on search term
    const filteredCountries = countries.filter((country) =>
        country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        country.code.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Get selected country name
    const selectedCountry = countries.find((country) => country.name === value);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm("");
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Auto-focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }
    }, [isOpen]);

    const handleSelect = (country) => {
        onChange({
            target: {
                name: name,
                value: country.name,
            },
        });
        setIsOpen(false);
        setSearchTerm("");
        setHoveredIndex(-1);
    };

    const handleToggle = () => {
        setIsOpen(!isOpen);
        if (!isOpen) {
            setSearchTerm("");
            setHoveredIndex(-1);
        }
    };

    return (
        <div className="country-dropdown-wrapper position-relative" ref={dropdownRef}>
            {label && (
                <label htmlFor={id} className="form-label fw-medium">
                    {label}
                    {required && <span className="text-danger ms-1">*</span>}
                </label>
            )}

            {/* Custom Dropdown Button */}
            <div
                className={`country-dropdown-toggle form-control d-flex justify-content-between align-items-center ${isOpen ? "active" : ""
                    }`}
                onClick={handleToggle}
            >
                <span className={`country-dropdown-value ${selectedCountry ? "selected" : ""}`}>
                    {selectedCountry ? (
                        <span className="d-flex align-items-center gap-2">
                            <span className="country-flag">{selectedCountry.code}</span>
                            <span>{selectedCountry.name}</span>
                        </span>
                    ) : (
                        placeholder
                    )}
                </span>
                <i className={`bi bi-chevron-${isOpen ? "up" : "down"} dropdown-icon`}></i>
            </div>

            {/* Dropdown Menu */}
            <div className={`country-dropdown-menu ${isOpen ? "show" : ""}`}>
                <div className="dropdown-menu-inner">
                    {/* Search Input */}
                    <div className="search-wrapper">
                        <div className="search-input-container">
                            <i className="bi bi-search search-icon"></i>
                            <input
                                ref={searchInputRef}
                                type="text"
                                className="form-control search-input"
                                placeholder="Search countries or codes..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                            />
                            {searchTerm && (
                                <i
                                    className="bi bi-x-circle clear-icon"
                                    onClick={() => setSearchTerm("")}
                                ></i>
                            )}
                        </div>
                    </div>

                    {/* Country List */}
                    <div className="country-list">
                        {filteredCountries.length > 0 ? (
                            filteredCountries.map((country, index) => (
                                <div
                                    key={country.code}
                                    className={`country-item ${value === country.name ? "selected" : ""
                                        } ${hoveredIndex === index ? "hovered" : ""}`}
                                    onClick={() => handleSelect(country)}
                                    onMouseEnter={() => setHoveredIndex(index)}
                                    onMouseLeave={() => setHoveredIndex(-1)}
                                >
                                    <div className="country-info">
                                        <span className="country-code-badge">{country.code}</span>
                                        <span className="country-name">{country.name}</span>
                                    </div>
                                    {value === country.name && (
                                        <i className="bi bi-check-circle-fill check-icon"></i>
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className="no-results">
                                <i className="bi bi-search"></i>
                                <p className="mb-0 mt-2">No countries found</p>
                                <small className="text-muted">Try a different search term</small>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CountryDropdown;
